import classnames from 'classnames';
import React from 'react';

import { Accordion, AccordionItem } from '../Accordion';
import { AlertFromDto } from '../AlertFromDto';
import { useCartApi } from '../api/useCartApi';
import { useAppContext } from '../AppContext';
import { Button } from '../Button';
import { CartCampaigns } from '../CartCampaigns';
import { CartPacks } from '../CartPacks';
import { CartSummary } from '../CartSummary';
import { Col } from '../Col';
import { appConfig } from '../config';
import { Container } from '../Container';
import { CartShippingProfilesFormHelper } from '../form/CartShippingProfilesForm';
import { TextInput } from '../form/fields/Input';
import { Radio } from '../form/fields/RadioCheck';
import { FormGroup } from '../form/FormGroup';
import { GenericCouponFormHelper } from '../form/GenericCouponForm';
import { GiftCardForm } from '../form/GiftCardForm';
import { InputGroup } from '../form/InputGroup';
import { LocaleForm } from '../form/LocaleForm';
import { PersonalCouponsFormHelper } from '../form/PersonalCouponsForm';
import { FreeShippingThreshold } from '../FreeShippingThreshold';
import { Div, H3, H5, H6, P, Span } from '../HTMLElement';
import { t } from '../i18n/i18n';
import { Icon } from '../Icon';
import { InstallmentPaymentBanner } from '../InstallmentPaymentBanner';
import { OffersList } from '../OffersList';
import { PaymentIcons } from '../PaymentIcons';
import { RecommendedCartProducts } from '../RecommendedCartProducts';
import { Link } from '../router/Link';
import { Row } from '../Row';
import { useCart } from '../useCart';
import { useModals } from '../useModals';
import { formatCurrency, formatHtml, renderUI } from '../util';
import { CartPageProps } from './CartPage';

export const CartPageA = ({
  cartSkusToMoveCampaignsCodes,
  cartTotalAlert,
  enableContinueShopping = false,
  enableLoyaltyPoint = true,
  enableStickySummary,
  freeShippingThresholdCriterion = 'currency',
  headerHeight,
  shippingAlert,
  showCampaignOfferOnMount,
  summaryBottomInfo,
  summaryTopInfo,
}: CartPageProps) => {
  const { blockingAlerts, campaignsCartSkus, canMoveOn, cart } = useCart({
    cartSkusToMoveCampaignsCodes,
    populate: {
      alerts: true,
      availableCoupons: true,
      billingAddress: true,
      campaigns: true,
      depositPayments: true,
      offers: true,
      packs: {
        alerts: true,
        cartSkus: {
          alerts: true,
          assembledComponents: {
            alerts: true,
            sku: {
              product: true,
            },
          },
          giftCard: true,
          offers: true,
          sku: {
            attributes: true,
            measurement: true,
            product: {
              attributes: true,
            },
          },
        },
      },
      paymentMethod: true,
      shippingAddress: true,
      shippingProfile: true,
      upsellProducts: {
        skus: {
          price: true,
        },
        variantProducts: {
          skus: true,
        },
      },
    },
  });

  const { info, shippingCountry } = useAppContext();

  const { removeCoupon, removeGiftCard } = useCartApi();

  const { showModal } = useModals();

  const handleChangeShippingCountryButtonClick = React.useCallback(
    () =>
      showModal({
        children: <ChangeShippingCountryModal />,
        title: t('cart.modal.shippingCountry.title'),
      }),
    [showModal],
  );

  return renderUI({
    bs5: (
      <>
        {enableStickySummary && cart?.itemsNumber && cart.itemsNumber > 0 && (
          <Div
            background="white"
            display={{ md: 'none', xs: 'block' }}
            marginBottom={3}
            paddingBottom={3}
            paddingTop={2}
            paddingX={3}
            style={{
              position: 'sticky',
              top: headerHeight,
              zIndex: 100,
            }}
            uiClassName={{ bs5: 'border-bottom shadow-sm' }}
          >
            <CartSummary
              blockingAlerts={blockingAlerts}
              canMoveOn={canMoveOn}
              cart={cart}
              cartTotalAlert={cartTotalAlert}
              enableContinueShopping={enableContinueShopping}
              enableLoyaltyPoint={enableLoyaltyPoint}
              forceVariant="B"
              freeShippingThresholdCriterion={freeShippingThresholdCriterion}
              shippingAlert={shippingAlert}
              summaryTopInfo={summaryTopInfo}
            />
          </Div>
        )}
        <Container fluid="xxl">
          <Row>
            {cart?.itemsNumber && cart.itemsNumber > 0 ? (
              <>
                <Col size={12} />
                <Col size={{ lg: 8, xs: 12 }}>
                  <>
                    {cart.packs && <CartPacks packs={cart.packs} />}
                    {cart.campaigns && (
                      <CartCampaigns
                        campaigns={cart.campaigns}
                        campaignsCartSkus={campaignsCartSkus}
                        showCampaignOfferOnMount={showCampaignOfferOnMount}
                      />
                    )}
                  </>

                  {appConfig.enableRecommendedCartProducts && (
                    <Div marginBottom={3}>
                      <RecommendedCartProducts />
                    </Div>
                  )}
                  <Div
                    background="white"
                    className="cart-footer"
                    display={{ md: 'block', xs: 'none' }}
                    marginBottom={3}
                    padding={3}
                  >
                    <H6>{t('cart.paymentMethods.title')}</H6>
                    <PaymentIcons />
                  </Div>
                </Col>
                <Col size={{ lg: 4, xs: 12 }}>
                  <CartSummary
                    blockingAlerts={blockingAlerts}
                    canMoveOn={canMoveOn}
                    cart={cart}
                    cartTotalAlert={cartTotalAlert}
                    enableContinueShopping={enableContinueShopping}
                    enableLoyaltyPoint={enableLoyaltyPoint}
                    freeShippingThresholdCriterion={freeShippingThresholdCriterion}
                    shippingAlert={shippingAlert}
                    summaryTopInfo={summaryTopInfo}
                  />
                </Col>
                <Col display={{ md: 'none' }} size={12}>
                  <Div background="white" className="cart-footer" marginBottom={3} padding={3}>
                    <H6>{t('cart.paymentMethods.title')}</H6>
                    <PaymentIcons width={50} />
                  </Div>
                </Col>
              </>
            ) : (
              cart?.itemsNumber === 0 && (
                <Col size={12}>
                  <Div background="white" padding={5} textAlign="center">
                    <H3>{t('cart.empty.title')}</H3>

                    <P dynamicMargin={0}>{t('cart.empty.description')}</P>
                    <Link className={classnames('btn btn-primary mt-3 mt-md-5')} to="/search">
                      {t('misc.discoverOurProducts')}
                    </Link>
                  </Div>
                </Col>
              )
            )}
          </Row>
        </Container>
      </>
    ),
  });
};

interface ChangeShippingCountryModalProps {}

const ChangeShippingCountryModal = (props: ChangeShippingCountryModalProps) => {
  return (
    <>
      <LocaleForm />
      <div className="text-center small pt-3">{t('cart.modal.shippingCountry.alert')}</div>
    </>
  );
};
